import { useRef, useState, type ReactElement } from 'react';
import styled from 'styled-components';
import { Stack } from '@ardoq/layout';
import { caption, Text } from '@ardoq/typography';
import { Link } from '@ardoq/link';
import { MarkdownViewer } from '@ardoq/markdown';
import { useIsTextTruncated } from '@ardoq/hooks';
import { colors, spacing } from '@ardoq/design-tokens';
import { t, type I18nLocale } from '@ardoq/i18n';
import { DESCRIPTION_CLAMP_LINES } from '../miniOverviewConsts';

const DescriptionMarkdown = styled(MarkdownViewer)`
  ${caption}
  color: ${colors.textModerate};

  p {
    margin: 0;
  }

  p + p {
    margin-top: ${spacing.s8};
  }
`;

const getToggleLabel = (
  isExpanded: boolean,
  i18nLocale: I18nLocale
): string => {
  if (isExpanded) {
    return t(
      i18nLocale,
      'componentOverview:miniOverview.showLess',
      'Show less'
    );
  }
  return t(i18nLocale, 'componentOverview:miniOverview.showMore', 'Show more');
};

type PreviewDescriptionProps = {
  description: string;
  i18nLocale: I18nLocale;
};

export const PreviewDescription = ({
  description,
  i18nLocale,
}: PreviewDescriptionProps): ReactElement => {
  const [isExpanded, setIsExpanded] = useState(false);
  const clampedRef = useRef<HTMLDivElement>(null);
  const isOverflowing = useIsTextTruncated(clampedRef, DESCRIPTION_CLAMP_LINES);
  const toggleExpanded = () => setIsExpanded(wasExpanded => !wasExpanded);
  const markdown = (
    <DescriptionMarkdown content={description} sanitationLevel="permissive" />
  );

  return (
    <Stack gap="xxsmall">
      {isExpanded ? (
        markdown
      ) : (
        <Text
          variant="caption"
          truncate={{ lines: DESCRIPTION_CLAMP_LINES, onlyEllipsis: true }}
          ref={clampedRef}
        >
          {markdown}
        </Text>
      )}
      {(isExpanded || isOverflowing) && (
        <Link
          hideIcon
          typography="caption"
          onClick={toggleExpanded}
          aria-expanded={isExpanded}
        >
          {getToggleLabel(isExpanded, i18nLocale)}
        </Link>
      )}
    </Stack>
  );
};
